import { useState } from "react";
import { motion } from "framer-motion";
import { ReceiptText, CalendarClock, BadgeCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const MIN = 500;
const MAX = 5000;
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export const Savings = () => {
  const [invoice, setInvoice] = useState(1450);
  const fill = ((invoice - MIN) / (MAX - MIN)) * 100;
  const monthly = invoice * 0.5;
  const annual = monthly * 12;

  return (
    <section id="savings" data-testid="savings-section" className="bg-white py-16 sm:py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <div>
          <Reveal>
            <p className="eyebrow text-brand-orange">The First-Client Advantage</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
              Up to 50% off your first premium — based on the invoice you already have.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg leading-relaxed text-slate-600">
              Switching shouldn't cost you a cent. Send us the last invoice from your current
              insurance provider and our team will beat it — first-time clients qualify for
              discounts of up to 50%, calculated directly from what you're paying today.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <ul className="mt-8 space-y-4">
              {[
                { icon: ReceiptText, text: "Discount based on your last insurance invoice" },
                { icon: CalendarClock, text: "Decision within one business day" },
                { icon: BadgeCheck, text: "Applies to your entire first policy term" },
              ].map((b) => (
                <li key={b.text} className="flex items-center gap-3 text-base font-medium text-brand-navy">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-brand-orange">
                    <b.icon className="h-4 w-4" />
                  </span>
                  {b.text}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-2xl shadow-slate-900/10 sm:p-9" data-testid="savings-calculator">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Savings estimator</p>
              <span className="rounded-full bg-orange-50 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-orange">
                Up to 50% off
              </span>
            </div>

            <label htmlFor="invoice-slider" className="mt-7 block text-sm font-semibold text-brand-navy">
              Your last monthly insurance invoice
            </label>
            <p className="mt-2 font-display text-5xl font-extrabold tracking-tight text-brand-navy" data-testid="calc-invoice-value">
              {fmt(invoice)}
              <span className="ml-2 font-mono text-sm font-medium text-slate-400">/ month</span>
            </p>
            <input
              id="invoice-slider"
              type="range"
              min={MIN}
              max={MAX}
              step={50}
              value={invoice}
              onChange={(e) => setInvoice(Number(e.target.value))}
              className="mt-6 w-full"
              style={{ "--fill": `${fill}%` }}
              data-testid="calc-slider"
            />
            <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-widest text-slate-400">
              <span>{fmt(MIN)}</span>
              <span>{fmt(MAX)}</span>
            </div>

            <div className="mt-7 space-y-4 rounded-2xl bg-brand-ice p-6">
              <div className="flex items-center justify-between" data-testid="calc-monthly">
                <span className="text-sm font-medium text-slate-600">Potential monthly savings, up to</span>
                <motion.span
                  key={monthly}
                  initial={{ opacity: 0.4, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="font-display text-2xl font-extrabold text-brand-blue"
                >
                  {fmt(monthly)}
                </motion.span>
              </div>
              <div className="h-px bg-blue-100" />
              <div className="flex items-center justify-between" data-testid="calc-annual">
                <span className="text-sm font-medium text-slate-600">Potential annual savings, up to</span>
                <motion.span
                  key={annual}
                  initial={{ opacity: 0.4, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="font-display text-2xl font-extrabold text-brand-orange"
                >
                  {fmt(annual)}
                </motion.span>
              </div>
            </div>

            <button
              data-testid="calc-cta"
              onClick={() => {
                window.__prefillInvoice = invoice;
                window.dispatchEvent(new Event("fleetguard:prefill-invoice"));
                scrollToId("quote");
              }}
              className="mt-7 w-full rounded-full bg-brand-orange px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-600/30 transition-all hover:-translate-y-0.5 hover:brightness-105"
            >
              Lock in my discount
            </button>
            <p className="mt-4 text-center text-xs leading-relaxed text-slate-400">
              Estimate only. Final discount is confirmed by underwriting review of your last insurance invoice.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
